import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { styled, Theme } from "@mui/material";
import CloseIconButton from "fe-common/ui/icons/closeIconButton";
import { OpenChatIcon } from "./openChatIcon";
import { commonBoxShadowStyleByOpacity } from "fe-common/ui/boxShadow";
import { DesktopBreakpoint, MobileBreakpoint } from "../layout/breakpoints";
import { isMobile } from "fe-common/utils/isMobile";
import { usePatient } from "../state/patientContext";
import clsx from "clsx";
import {
  KahunaWidgetConfiguration,
  WidgetAlignment,
  WidgetButtonLayout,
} from "../d";
import { GenericButton } from "../components/uiControllers/genericButton";
import { useTranslate } from "../hooks/useTranslate";
import { dispatchKahunEvent, KahunApiEvents } from "../events/events";
import { ConversationStatus } from "../driver/types/driverTypes";
import { onRtl } from "fe-web-components/rtl/rtlStyles";
import { KahunWidgetNavigationBar } from "./kahunWidgetNavigationBar";

//region [[ Styles ]]

const KahunContainer = styled("div")(({ theme }) => ({
  position: "fixed",
  zIndex: 1500,
  minWidth: "220px",
  height: "auto",
  left: "0",
  bottom: "14px",
  "&.open": {
    height: "calc(100% - 14px)",
  },
  "&.open.no-top-panel": {
    height: "100%",
  },
  [`&.${WidgetAlignment.center}.open, &.${WidgetAlignment.fullscreen}.open`]: {
    position: "absolute",
    backgroundColor: "rgb(0 0 0 / 50%)",
    left: 0,
    top: 0,
    width: "100%",
    height: "100%",
  },
  [`&.${WidgetAlignment.center}.open`]: {
    position: "fixed",
  },
  [`&.${WidgetAlignment.right}`]: {
    left: "auto",
    right: 0,
  },
  [`&.${WidgetAlignment.inline}`]: {
    position: "static",
  },
  [theme.breakpoints.down(DesktopBreakpoint)]: {
    "&.open": {
      minWidth: "430px",
    },
    [theme.breakpoints.down(MobileBreakpoint)]: {
      zIndex: 1500,
      "&.open": {
        minWidth: "100%",
        overflow: "hidden",
      },
    },
  },
}));

const StaticButtonContainer = styled("div")({
  display: "flex",
  position: "static",
  width: "fit-content",
});

const ChatWidgetContainerView = styled("div")(
  ({
    theme,
    minimized,
    isopen,
  }: {
    theme?: Theme;
    minimized: 0 | 1;
    isopen: 0 | 1;
  }) => ({
    position: "absolute",
    bottom: 0,
    left: 0,
    margin: "14px",
    zIndex: 1500,
    width: "350px",
    height: "670px",
    display: "flex",
    flexFlow: "column",
    maxHeight: "100%",
    [`&.${WidgetAlignment.right}`]: {
      left: "auto",
      right: 0,
    },
    [`&.${WidgetAlignment.center}`]: {
      width: "600px",
      height: "90%",
      margin: 0,
      borderRadius: "14px",
      overflow: "hidden",
      overflowY: "auto",
      left: "calc(50% - 300px)",
      top: "5%",
      ...commonBoxShadowStyleByOpacity("0.6"),
    },
    [`&.${WidgetAlignment.fullscreen}`]: {
      position: "fixed",
      top: "auto",
      bottom: "1%",
      left: 0,
      width: "50%",
      height: "98%",
      margin: "0 25%",
      borderRadius: "14px",
      overflow: "hidden",
      ...commonBoxShadowStyleByOpacity("0.6"),
      transition: "height 0.5s ease-out",
    },
    [`&.${WidgetAlignment.inline}`]: {
      position: "static",
      right: "auto",
      left: "auto",
      width: "100%",
      minWidth: 350,
      minHeight: "100%",
      margin: 0,
    },
    [theme.breakpoints.down("sm")]: {
      [`&, &.${WidgetAlignment.center}, &.${WidgetAlignment.fullscreen}`]: {
        top: "auto",
        bottom: 0,
        left: 0,
        width: "calc(100% - 8px)",
        height: "95%",
        margin: "4px",
        borderRadius: "14px",
        overflow: "hidden",
        ...commonBoxShadowStyleByOpacity("0.6"),
        transition: "height 0.5s ease-out",
        "&[minimized='1']": { height: "55%" },
        [`&.${WidgetAlignment.fullscreen}`]: {
          height: "calc(100% - 8px)",
        },
      },
    },
    // Infomed hard-codded hack to hide commercial popups while open
    "#ZA_CAMP_CONTAINER": {
      display: isopen ? "none" : "inherit",
    },
    "#ZA_CAMP_BG": {
      display: isopen ? "none" : "inherit",
    },
  }),
);

export const WidgetTopBarHeight = 32;

const TopBarPanel = styled("div")({
  background: "#16C1F3", //PatientColorMap.GradLight,
  display: "flex",
  width: "100%",
  flexFlow: "row-reverse",
  height: `${WidgetTopBarHeight}px`,
  // Keep the X always to the right
  ...onRtl({
    flexFlow: "row",
  }),
});

const OpenWidgetIcon = styled(OpenChatIcon)({
  "&.open-chat-icon": {
    position: "absolute",
    right: "39px",
    bottom: "107px",
    color: "#0092b6",
    zIndex: 1500,
    left: 0,
  },
  "&.open-chat-icon.static": {
    position: "static",
    left: "auto",
    right: "auto",
    color: "#0092b6",
    zIndex: 1500,
  },
});

const OpenChatTextButton = styled(GenericButton)({
  "&.generic-button": {
    backgroundColor: "#0092b6",
    "&:hover, &:active, &:focus": {
      backgroundColor: "#0092b6",
    },
  },
});

const CloseIcon = styled(CloseIconButton)({
  "&.widget-close, &.widget-close:hover, &.widget-close:active, &.widget-close:focus": {
    position: "static",
    width: "16px",
    height: "16px",
    margin: "4px",
    padding: "12px",
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface ChatWidgetContainerProps {
  className?: string;
  onClose?: () => void;
  children?: React.ReactNode;
  sessionId?: string;
  chatIcon?: string;
  startAsOpen: boolean;
  // Current route
  initialEntry?: object;
  buttonLayout: WidgetButtonLayout;
  alignment: WidgetAlignment;
  buttonText?: string;
  allowClose?: boolean;
  configObject: KahunaWidgetConfiguration;
  navigationBar?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const CHAT_IS_OPEN_SESSION_STORAGE_KEY = "chatIsOpen";

export const ChatWidgetContainer = ({
  sessionId = null,
  startAsOpen = false,
  initialEntry = null,
  allowClose = true,
  ...props
}: ChatWidgetContainerProps) => {
  /* Did an earlier session kept the chat open? persist the state */
  const wasItOpen = JSON.parse(
    sessionStorage.getItem(
      (sessionId || "") + CHAT_IS_OPEN_SESSION_STORAGE_KEY,
    ),
  );

  /* If the partner asked for it to start open, just start open regardless */
  const [isOpen, setIsOpen] = useState(wasItOpen || startAsOpen);

  const minimized = !!sessionStorage.getItem("diseaseClicked");

  const [isMinimized, setIsMinimized] = useState(minimized);

  // Make sure to sync state with props in case the app re-renders to support
  // starting the app from an outside source even if the chat was rendered and
  // closed before
  useEffect(() => {
    setIsOpen(startAsOpen);
  }, [startAsOpen, props.configObject]);

  const {
    agreeOnTerms,
    setAgreeOnTerms,
    setAbandoned,
    conversationStatus,
  } = usePatient();

  const { translate } = useTranslate();

  const showTopBar =
    allowClose && props.alignment !== WidgetAlignment.fullscreen;

  useEffect(() => {
    // In case we are open, and the initial entry is not "/", mark the state as already
    // agreed on the terms so that the conversation could initialize
    if (
      isOpen &&
      initialEntry &&
      Object.keys(initialEntry).length &&
      !agreeOnTerms
    ) {
      setAgreeOnTerms(true);
    }
  }, [isOpen, agreeOnTerms, initialEntry, setAgreeOnTerms]);

  // dispatch events
  useEffect(() => {
    dispatchKahunEvent(KahunApiEvents.widget, {
      state: isOpen ? "open" : "closed",
      status: conversationStatus,
    });
  }, [isOpen, conversationStatus]);

  // Report to parent so that everything could be saved
  const handleClose = () => {
    // If the user haven't even started the conversation just close the window
    if (!agreeOnTerms) {
      setIsOpen(false);
      props.onClose?.();
      return;
    }
    (async () => {
      try {
        if (conversationStatus !== ConversationStatus.COMPLETED) {
          await setAbandoned(true);
        }
      } catch (e) {
      } finally {
        setIsOpen(false);
        props.onClose?.();
      }
    })();
  };

  // Save to persist through navigation
  useEffect(() => {
    sessionStorage.setItem(
      (sessionId || "") + CHAT_IS_OPEN_SESSION_STORAGE_KEY,
      JSON.stringify(isOpen),
    );
  }, [isOpen, sessionId]);

  // Disable scroll in mobile
  useEffect(() => {
    const $body = document.getElementsByTagName("body")[0];
    let scrollPosition = 0;

    const disableScroll = () => {
      scrollPosition = window.pageYOffset;
      $body.style.overflow = "hidden";
      $body.style.position = "fixed";
      $body.style.top = `-${scrollPosition}px`;
      $body.style.width = "100%";
      $body.style.height = "100%";
    };

    const enableScroll = () => {
      $body.style.removeProperty("overflow");
      $body.style.removeProperty("position");
      $body.style.removeProperty("top");
      $body.style.removeProperty("width");
    };
    if (isOpen && isMobile.any()) {
      disableScroll();
    } else if (isMobile.any()) {
      enableScroll();
    }
  }, [isOpen]);

  const clearMinimized = useCallback(() => {
    setIsMinimized(false);
    sessionStorage.removeItem("diseaseClicked");
  }, [setIsMinimized]);

  if (!isOpen) {
    if (props.buttonLayout === WidgetButtonLayout.noButton) return null;
    if (props.buttonLayout === WidgetButtonLayout.floatingTextButton) {
      return (
        <KahunContainer
          className={clsx(
            "kahun-container",
            "start-chat-button",
            props.alignment,
          )}
        >
          <OpenChatTextButton
            className={"open-kahun-button"}
            onClick={() => setIsOpen(true)}
            label={props.buttonText || translate("START_CHAT_BUTTON")}
          />
        </KahunContainer>
      );
    }

    if (props.buttonLayout === WidgetButtonLayout.textButton) {
      return (
        <StaticButtonContainer className={"start-chat-button"}>
          <OpenChatTextButton
            className={"open-kahun-button"}
            onClick={() => setIsOpen(true)}
            label={props.buttonText || translate("START_CHAT_BUTTON")}
          />
        </StaticButtonContainer>
      );
    }
    if (props.buttonLayout === WidgetButtonLayout.staticIcon) {
      return (
        <StaticButtonContainer className={"start-chat-button"}>
          <OpenWidgetIcon
            className={clsx("open-kahun-icon", "static")}
            onClick={() => setIsOpen(true)}
            imgUrlSrc={props.chatIcon}
          />
        </StaticButtonContainer>
      );
    }
    // Else =WidgetButtonLayout.icon || didn't match any other
    if (props.buttonLayout !== WidgetButtonLayout.icon) {
      console.warn(
        `Error unknown button layout "${
          props.buttonLayout
        }", please use one of the following: [${Object.values(
          WidgetButtonLayout,
        ).join("|")}] using default layout: "${
          WidgetButtonLayout.icon
        }" instead`,
      );
    }
    return (
      <KahunContainer
        className={clsx(
          "kahun-container",
          "start-chat-button",
          props.alignment,
        )}
      >
        <OpenWidgetIcon
          className={"open-kahun-icon"}
          onClick={() => setIsOpen(true)}
          imgUrlSrc={props.chatIcon}
        />
      </KahunContainer>
    );
  }

  return (
    <KahunContainer
      className={clsx(
        "kahun-container",
        isOpen ? " open" : "",
        showTopBar ? "" : "no-top-panel",
        props.alignment,
      )}
    >
      <ChatWidgetContainerView
        className={clsx(
          props.className,
          "chat-container",
          showTopBar ? "" : "no-top-panel",
          props.alignment,
        )}
        minimized={isMinimized ? 1 : 0}
        isopen={isOpen ? 1 : 0}
        onClick={clearMinimized}
      >
        {props.navigationBar && <KahunWidgetNavigationBar />}
        {showTopBar && (
          <TopBarPanel>
            <CloseIcon className={"widget-close"} onClick={handleClose} />
          </TopBarPanel>
        )}
        {props.children}
      </ChatWidgetContainerView>
    </KahunContainer>
  );
};
